/**
 * Data layer behind the Agent Chain breadcrumb (PT-5713).
 *
 * The breadcrumb shows the path from the rendered root down to the node whose KPI panel is open —
 * "Root → … → Me" — so an agent deep in the tree can see who sits upstream of them and where fees
 * flow. The chain is derived purely from the `parent` back-links the tree already carries
 * ({@link buildOfferTree}); nothing here issues a network call, so it is stable per node id.
 *
 * Every ancestor produced here is, by construction, a node already rendered in the viewer's tree —
 * a walk of `parent` can only reach nodes that were loaded — so each upstream pill is a valid scroll
 * target the viewer owns. The current node is marked so the panel can emphasise it and stop it from
 * scrolling to itself.
 */
import {OfferNode} from '../types'

/** Shown when a node carries no owner (manager) name and no offer name to fall back on. */
export const UNNAMED_OWNER = '—'

/** Above this many pills the middle of the chain collapses behind an expandable ellipsis. */
export const MAX_UNCOLLAPSED_PILLS = 5

/**
 * One node on the Agent Chain breadcrumb.
 */
export type AgentChainPill = {
  /** Stable id of the node this pill points at — the scroll target and part of the React key. `null` for an offer with no id, which cannot be scrolled to. */
  offerId: string | null
  /** The node owner's (manager's) display name, falling back to the offer name, then a dash. */
  ownerName: string
  /** Depth relative to the rendered root (root = 0), shown as "Lvl N". */
  level: number
  /** The node whose panel this breadcrumb belongs to: emphasised, and never a scroll target. */
  isCurrent: boolean
  /** Whether clicking the pill scrolls the tree to this node — an upstream ancestor with a real id. */
  isClickable: boolean
}

/**
 * The collapsed shape of a long chain: the root, the hidden middle (revealed on expand) and the
 * last two pills (the immediate parent and the current node), matching the AC's
 * "Root → … → Parent → Me".
 */
export type CollapsedChain = {
  head: AgentChainPill
  hidden: AgentChainPill[]
  tail: AgentChainPill[]
}

/**
 * Builds the ordered Agent Chain for a node: root first (index 0), the node itself last.
 *
 * Walks the `parent` back-links up to the rendered root, then reverses so the array reads
 * top-down. Every entry is a loaded node, so all but the last are clickable scroll targets.
 *
 * @param node - The node whose panel is open (the "Me" end of the chain).
 * @returns The pills from root to current node, in that order.
 */
export const buildAgentChain = (node: OfferNode): AgentChainPill[] => {
  const lineage: OfferNode[] = []
  for (let current: OfferNode | undefined = node; current; current = current.parent) lineage.push(current)
  lineage.reverse()

  return lineage.map((ancestor) => {
    const offerId = ancestor.offer.id != null ? String(ancestor.offer.id) : null
    return {
      offerId,
      ownerName: ancestor.offer.managerName || ancestor.offer.name || UNNAMED_OWNER,
      level: ancestor.level,
      isCurrent: ancestor === node,
      // Clickable when it is an upstream ancestor with a real id to scroll to. Every ancestor a
      // `parent` walk reaches is loaded in the viewer's tree, so "reachable" == "owned" today; a
      // future unowned ancestor would set this false and fall through to a static (non-link) pill.
      isClickable: ancestor !== node && offerId != null,
    }
  })
}

/**
 * Splits a chain into its collapsed form when it is longer than {@link MAX_UNCOLLAPSED_PILLS},
 * otherwise returns `null` (render every pill).
 *
 * The collapsed form keeps the root and the last two pills visible and hides everything between,
 * so a deep chain still shows where it starts and ends without a wall of pills.
 *
 * @param pills - The full chain from {@link buildAgentChain}.
 * @returns The collapsed split, or `null` when the chain is short enough to show in full.
 */
export const collapseAgentChain = (pills: AgentChainPill[]): CollapsedChain | null => {
  if (pills.length <= MAX_UNCOLLAPSED_PILLS) return null
  return {
    head: pills[0],
    hidden: pills.slice(1, -2),
    tail: pills.slice(-2),
  }
}
